/**
 * Artifacts - the research outputs that are documents rather than replies.
 *
 * A `draft`, a `chronology` and a `memo`/`deep_research` are standalone
 * deliverables: long, structured, meant to be read, exported and returned to.
 * They open in the side pane. Conversational shapes (`qa`, `overview`,
 * `bullets`, `find_document`) stay inline where the conversation is.
 *
 * Nothing here fetches. An artifact IS a turn's already-hydrated envelope, so
 * reopening one from history costs nothing - the lazy blob hydration that
 * paints the transcript is the same data the pane renders.
 */

import { ResearchIntent, ResearchResponse } from './types';
import { AssistantTurn, ResearchTurn, isAssistantTurn } from './turns';

/** Intents whose output belongs in the pane. */
const ARTIFACT_INTENTS: ReadonlySet<ResearchIntent> = new Set<ResearchIntent>([
  'draft',
  'chronology',
  'memo',
  'deep_research',
]);

export function isArtifactIntent(intent: ResearchIntent | null | undefined): boolean {
  return !!intent && ARTIFACT_INTENTS.has(intent);
}

/** Short type label for the badge. Untranslated - run through `t()`. */
export function artifactTypeLabel(intent: ResearchIntent | null | undefined): string {
  switch (intent) {
    case 'draft':
      return 'Document';
    case 'chronology':
      return 'Timeline';
    case 'memo':
      return 'Memo';
    case 'deep_research':
      return 'Research memo';
    default:
      return 'Result';
  }
}

/**
 * The artifact's display title.
 *
 * Prefers the document's OWN title from the typed payload; falls back to the
 * SQL summary's first line, which is available before the blob is hydrated,
 * and finally to the type label. The row does not carry a document title
 * today - surfacing one server-side would sharpen the pre-hydration label,
 * but nothing depends on it.
 */
export function artifactTitle(turn: AssistantTurn): string {
  // The engine's own document title wins when it sent one.
  const fromEngine = cleanTitle(turn.response?.documentTitle);
  if (fromEngine) return fromEngine;

  const payload = turn.response?.payload as Record<string, any> | undefined;
  const fromPayload = cleanTitle(payload?.title || payload?.document_type);
  if (fromPayload) return fromPayload;

  // The summary is sometimes the raw JSON blob, whose first line is a lone
  // "{" - which is how a brace ended up as the document title on screen.
  const fromSummary = cleanTitle(firstProseLine(turn.summary));
  if (fromSummary) return fromSummary;

  // The chat's own name is a real, human title; better than a type label.
  const fromChat = cleanTitle(turn.response?.meta.chatName);
  if (fromChat) return fromChat;

  return artifactTypeLabel(turn.response?.intent ?? turn.intent ?? null);
}

/**
 * A title candidate, or '' when the value is JSON furniture rather than text.
 *
 * Anything that is punctuation-only, or opens a JSON object/array, is not a
 * title - it is the first line of a serialised payload.
 */
export function cleanTitle(value: unknown): string {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  if (!text) return '';
  if (/^[{}[\]"',:]+$/.test(text)) return '';
  if (/^[{[]/.test(text)) return '';
  return text.length > 80 ? `${text.slice(0, 77)}…` : text;
}

/** First line of a summary that reads as prose, skipping JSON furniture. */
function firstProseLine(summary?: string): string {
  const lines = (summary || '').split('\n').map((l) => l.trim()).filter(Boolean);
  return lines.find((line) => !/^[{}[\]"',:]/.test(line)) || '';
}

/** One-line preview for the inline card. */
export function artifactPreview(turn: AssistantTurn): string {
  // Mid-eager-fetch the document is moments away; a summary shown now is
  // only replaced, which is the flicker.
  if (!turn.response && turn.hydration === 'loading') return '';
  const payload = turn.response?.payload as Record<string, any> | undefined;

  const candidates: Array<unknown> = [
    payload?.summary,
    payload?.body_markdown,
    payload?.executive_summary?.direct_answer,
    turn.response?.markdown,
    turn.summary,
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    // Strip markdown furniture so the preview reads as prose.
    const text = candidate
      .replace(/^#+\s*/gm, '')
      .replace(/[*_`>]/g, '')
      .split('\n')
      .map((l) => l.trim())
      .find(Boolean);
    if (text) return text.length > 140 ? `${text.slice(0, 137)}…` : text;
  }
  return '';
}

/* ------------------------------------------------------------------ */
/*  Collecting a chat's artifacts                                      */
/* ------------------------------------------------------------------ */

export interface Artifact {
  /** The turn this artifact lives on - its identity in the pane. */
  turnId: string;
  messageUuid?: string;
  intent: ResearchIntent;
  /** Which document this is a version OF. Same key = same document. */
  documentKey: string;
  title: string;
  preview: string;
  /** Absent while the turn is streaming or its blob is not yet hydrated. */
  response?: ResearchResponse;
  /** Still being produced. */
  isStreaming: boolean;
  /**
   * Version within this chat: artifacts of the SAME intent are successive
   * versions, in turn order, because re-asking produces a new turn and a new
   * blob. 1-based.
   */
  version: number;
  versionCount: number;
}

/* ------------------------------------------------------------------ */
/*  Has the OUTPUT SHAPE been decided yet?                             */
/* ------------------------------------------------------------------ */

/**
 * The slot the engine asks about when it cannot pick the output shape itself.
 * `L2_OUTPUT_FORMAT` fills this, and its values name a document or a reply.
 */
export const FORMAT_SLOT = 'format';

/** Format values that mean "answer me here", not "write me a document". */
const CONVERSATIONAL_FORMATS = new Set(['answer', 'chat', 'conversation', 'conversational', 'reply']);

/**
 * Stages that can only happen AFTER the format question could still be asked.
 *
 * The engine holds the turn BEFORE retrieval (`[CLARIFY_L2B] holding the turn
 * before retrieval`), so `started` / `intent` / `orchestration` may all have
 * gone by with a format hold still to come. Once a source has actually been
 * searched, it has not.
 */
const POST_HOLD_STAGES = new Set(['retrieval', 'evaluation', 'synthesis', 'verification', 'completed']);

/** The format the user picked, once they have picked one. */
export function chosenOutputFormat(turn: AssistantTurn): string {
  const clarification = turn.clarification;
  if (!clarification?.settled) return '';
  const question = (clarification.pending.questions || []).find(
    (q) => q.slot === FORMAT_SLOT
  );
  if (!question) return '';
  const draft = clarification.draft || {};
  return String(draft[question.id] ?? draft[question.slot] ?? '').trim();
}

/**
 * Is it settled that this turn produces a document?
 *
 * WHY THIS EXISTS. A `deep_research` turn used to put the artifact card in the
 * transcript and open the document pane the instant `research.intent` arrived,
 * on the reasoning that the shape is known before the content is. For a turn
 * the engine is about to HOLD, it is not known: the engine stops before
 * retrieval precisely to ask whether the user wants a memo or an answer in the
 * chat - and by then they have already watched it "start creating a document"
 * it may never write. Worse, answering "just answer in chat" left the card and
 * the pane sitting there, because the intent that put them there never changed.
 *
 * So the artifact affordance waits for one of three things to be true:
 * the answer exists and says so; the user chose a document; or the run is past
 * the point where it could still be asked.
 */
export function artifactShapeIsSettled(turn: ResearchTurn): boolean {
  if (!isAssistantTurn(turn)) return false;
  // Waiting on the user: the engine has said out loud that it has not decided.
  if (turn.clarification && !turn.clarification.settled) return false;

  const chosen = chosenOutputFormat(turn);
  if (chosen) return !CONVERSATIONAL_FORMATS.has(chosen.toLowerCase());

  // The finished thing is its own evidence.
  if (turn.response) return true;

  /*
    NARROW ON PURPOSE. Only a LIVE `deep_research` run can still be asked which
    shape to take: it is the one intent the engine reaches without the user
    having named an output, so it is the one it holds on. A draft, a
    chronology or a history turn awaiting hydration was never in question and
    keeps the standing behaviour - the card stands in from the start.
  */
  // `turn.response` is already ruled out above, so the intent is the run's.
  if (turn.intent !== 'deep_research') return true;
  if (!turn.live || turn.live.done) return true;

  return (turn.live.stages || []).some((stage: any) =>
    POST_HOLD_STAGES.has(String(stage?.key || ''))
  );
}

/**
 * Does this turn produce an artifact?
 *
 * The engine's `is_artifact` is authoritative when it sent one: it comes from
 * the same classification that picked the payload schema, so a conversational
 * answer can never be talked into a card by a stale intent guess on our side.
 * Only before an answer exists do we fall back to the intent - that is the
 * streaming case, where `research.intent` is all we have.
 */
export function turnIsArtifact(turn: ResearchTurn): turn is AssistantTurn {
  if (!isAssistantTurn(turn)) return false;
  if (turn.response) {
    const verdict = turn.response.meta.isArtifact;
    if (typeof verdict === 'boolean') return verdict;
    return isArtifactIntent(turn.response.intent);
  }
  // Before an answer exists the intent is a GUESS, and a user who has just
  // asked for a reply in the chat has overruled it.
  const chosen = chosenOutputFormat(turn);
  if (chosen) return !CONVERSATIONAL_FORMATS.has(chosen.toLowerCase());
  return isArtifactIntent(turn.intent);
}

/**
 * Every artifact in the transcript, oldest first, with version numbering per
 * intent. Pure - derived from turns already in memory.
 */
export function collectArtifacts(turns: ResearchTurn[]): Artifact[] {
  // A turn whose shape is still open is not an artifact yet - it must not put
  // a card in the transcript, and must not auto-open the document pane.
  const artifactTurns = (turns || []).filter(
    (turn): turn is AssistantTurn => turnIsArtifact(turn) && artifactShapeIsSettled(turn)
  );

  // Count per DOCUMENT so each entry knows the total for its own family —
  // not per intent, which grouped unrelated documents together.
  const totals = new Map<string, number>();
  artifactTurns.forEach((turn) => {
    const key = documentIdentity(turn);
    totals.set(key, (totals.get(key) || 0) + 1);
  });

  const seen = new Map<string, number>();
  return artifactTurns.map((turn) => {
    const intent = (turn.response?.intent ?? turn.intent) as ResearchIntent;
    const key = documentIdentity(turn);
    const version = (seen.get(key) || 0) + 1;
    seen.set(key, version);

    return {
      turnId: turn.id,
      messageUuid: turn.messageUuid,
      intent,
      documentKey: key,
      title: artifactTitle(turn),
      preview: artifactPreview(turn),
      response: turn.response,
      // A response IS the completion. Deriving "still writing" from the live
      // flag alone left a card spinning forever when a run's `done` never
      // arrived (a dropped final event, or the mirror detaching first).
      isStreaming: !turn.response && !!turn.live && !turn.live.done,
      version,
      versionCount: totals.get(key) || 1,
    };
  });
}

/**
 * The identity of the DOCUMENT a turn produced.
 *
 * Versioning used to group by intent, which folded any second draft-intent
 * turn into "v2" of the first - including an unrelated one. A version is the
 * same document iterated, so identity is the document's own title (the
 * engine's `documentTitle`, else the payload title), normalised. Two turns
 * that produced differently-named documents are two artifacts, not two
 * versions of one.
 */
export function documentIdentity(turn: AssistantTurn): string {
  // The TYPE is part of the identity. A timeline and a draft are different
  // deliverables even when they carry the same title - and they do, because
  // the engine can anchor a follow-up's title to the previous document. A
  // chronology must never be able to become "v2" of a draft.
  const type = (turn.response?.intent ?? turn.intent ?? 'unknown') as string;

  const title =
    cleanTitle(turn.response?.documentTitle) ||
    cleanTitle((turn.response?.payload as any)?.title) ||
    cleanTitle((turn.response?.payload as any)?.document_type);
  if (title) return `${type}:title:${title.toLowerCase().replace(/\s+/g, ' ').trim()}`;

  // No title to compare: the turn is its own artifact. Grouping unnamed
  // documents together is exactly the over-grouping being fixed.
  return `${type}:turn:${turn.id}`;
}

/** Earlier versions of the same artifact family, for the version switcher. */
export function versionsOf(artifacts: Artifact[], documentKey: string): Artifact[] {
  return artifacts.filter((a) => a.documentKey === documentKey);
}

/**
 * One entry per DOCUMENT - the newest version of each.
 *
 * The chat's list of generated documents was showing every version of every
 * pane: three re-drafts of one memo read as three separate documents, and the
 * list grew with each iteration instead of staying the shape of the work.
 * A document is one thing; its versions are a detail INSIDE it, and the pane
 * already has a version toggle for exactly that.
 *
 * Order follows first appearance, so the list does not reshuffle when an
 * existing document gains a version.
 */
export function latestArtifacts(artifacts: Artifact[]): Artifact[] {
  const newest = new Map<string, Artifact>();
  const order: string[] = [];

  (artifacts || []).forEach((artifact) => {
    const key = artifact.documentKey;
    if (!newest.has(key)) order.push(key);
    const current = newest.get(key);
    // Highest version wins; on a tie the later entry does, which is the one
    // that came later in the transcript.
    if (!current || artifact.version >= current.version) newest.set(key, artifact);
  });

  return order.map((key) => newest.get(key)!).filter(Boolean);
}
